import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  SelectChangeEvent,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useMonthlyTransactionsQuery } from 'store/api/sessionsSlice';
import { useTranslation } from 'providers/CultureProvider';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyTransactionsChart: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  
  // Generate last 5 years including current year
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);
  
  // Use the RTK Query hook
  const { data: chartData, isLoading: loading, error } = useMonthlyTransactionsQuery(selectedYear);

  const handleYearChange = (event: SelectChangeEvent<number>): void => {
    setSelectedYear(Number(event.target.value));
  };

  // Chart configuration for dual axis
  const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${t('Monthly Sessions & New Users')} - ${selectedYear}`,
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#333',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 4,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y;
            
            return `${datasetLabel}: ${value !== null ? value.toLocaleString() : 0}`;
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: t('Transactions'),
          color: '#666',
          font: {
            weight: 'bold',
          },
        },
        ticks: {
          color: '#FF6384',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: t('New Users'),
          color: '#666',
          font: {
            weight: 'bold',
          },
        },
        ticks: {
          color: '#36A2EB',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      x: {
        title: {
          display: true,
          text: t('Months'),
          color: '#666',
          font: {
            weight: 'bold',
          },
        },
        ticks: {
          color: '#666',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  }), [selectedYear, t]);

  // Memoize chart data with both datasets
  const barChartData = useMemo(() => ({
    labels: chartData?.monthlyData.map(item => item.shortMonthName) || [],
    datasets: [
      {
        label: t('Sessions'),
        data: chartData?.monthlyData.map(item => item.transactionCount) || [],
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: t('New Users'),
        data: chartData?.monthlyData.map(item => item.usersCount) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ],
  }), [chartData, t]);

  // Memoize summary calculations using the totalUsers from API
  const summaryData = useMemo(() => {
    if (!chartData) return null;
    
    return {
      totalTransactions: chartData.totalTransactions,
      averageTransactions: Math.round(chartData.totalTransactions / 12),
      totalUsers: chartData.totalUsers,
      averageUsers: Math.round(chartData.totalUsers / 12),
    };
  }, [chartData]);

  // Determine what to render - MAIN CONTENT
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={300}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box 
          height={300} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Alert 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {'status' in error
              ? `${t('Error')}: ${error.status}`
              : t('Failed to load data')
            }
          </Alert>
        </Box>
      );
    }

    if (!chartData) {
      return (
        <Box 
          height={300} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Typography color="text.secondary">
            {t('No data available')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box height={300}>
        <Bar data={barChartData} options={chartOptions} />
      </Box>
    );
  };

  // Separate function for summary to avoid nested conditionals
  const renderSummary = () => {
    if (loading || error || !chartData || !summaryData) {
      return null;
    }

    return (
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" flexDirection="column" alignItems="center" flex={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('Total Sessions')}
          </Typography>
          <Typography variant="h6" color="#FF6384" fontWeight={600}>
            {summaryData.totalTransactions.toLocaleString()}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" flex={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('Avg Sessions/Month')}
          </Typography>
          <Typography variant="h6" color="#FF6384" fontWeight={600}>
            {summaryData.averageTransactions.toLocaleString()}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" flex={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('Total New Users')}
          </Typography>
          <Typography variant="h6" color="#36A2EB" fontWeight={600}>
            {summaryData.totalUsers.toLocaleString()}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" flex={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('Avg Users/Month')}
          </Typography>
          <Typography variant="h6" color="#36A2EB" fontWeight={600}>
            {summaryData.averageUsers.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header with Title and Year Selector */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            {t('Monthly Analytics')}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label">{t('Year')}</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label={t('Year')}
              onChange={handleYearChange}
            >
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {renderMainContent()}
        {renderSummary()}
      </CardContent>
    </Card>
  );
};

export default MonthlyTransactionsChart;